import React, { useEffect, useMemo, useState } from "react";
import { isoDate, prettifyStoreName, fmtKr, ALL_STORES } from "./workshopCapacityUtils";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const MONTH_NAMES = [
  "Januar", "Februar", "Mars", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Desember",
];

const METODE_LABEL = {
  vipps: "Vipps",
  kortterminal: "Kortterminal",
};

const STATUS_LABEL = {
  betalt: "Betalt",
  refundert: "Refundert",
};

function daysInMonth(year, monthIndex0) {
  return new Date(year, monthIndex0 + 1, 0).getDate();
}

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

function fmtDate(d) {
  const dt = d ? new Date(d) : null;
  return dt && !isNaN(+dt)
    ? dt.toLocaleDateString("no-NO", { day: "2-digit", month: "2-digit", year: "numeric" })
    : "—";
}

function fmtDateTime(d) {
  const dt = d ? new Date(d) : null;
  return dt && !isNaN(+dt)
    ? dt.toLocaleString("no-NO", {
        day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
      })
    : "—";
}

function fmtKr2(n) {
  return `${(Number(n) || 0).toLocaleString("nb-NO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr`;
}

function csvNum(n) {
  return round2(n).toString().replace(".", ",");
}

function downloadCsv(filename, headers, dataRows) {
  const escape = (v) => {
    const s = String(v ?? "");
    return /[;"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers, ...dataRows].map((row) => row.map(escape).join(";"));
  const csv = "﻿" + lines.join("\r\n"); // BOM slik at Excel leser æøå riktig
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function receiptStatus(row) {
  if (row.status === "refundert") {
    return row.refundertBelop > 0 && row.refundertBelop < row.belop
      ? "delvis_refundert"
      : "refundert";
  }
  return "betalt";
}

const RECEIPT_STATUS_LABEL = {
  betalt: "Betalt",
  refundert: "Refundert",
  delvis_refundert: "Delvis refundert",
};

const RECEIPT_STATUS_STYLE = {
  betalt: "bg-emerald-100 text-emerald-800",
  refundert: "bg-orange-100 text-orange-800",
  delvis_refundert: "bg-orange-100 text-orange-800",
};

export function PaymentDetailModal({ row, onClose, onRefunded }) {
  const [showDetails, setShowDetails] = useState(false);
  const [showRefundForm, setShowRefundForm] = useState(false);
  const [refundAmount, setRefundAmount] = useState(0);
  const [refunding, setRefunding] = useState(false);
  const [refundError, setRefundError] = useState("");

  useEffect(() => {
    setShowRefundForm(false);
    setRefundError("");
    setRefundAmount(row?.belop || 0);
  }, [row?.id, row?.belop]);

  if (!row) return null;

  const status = receiptStatus(row);
  const selger = row.selger;

  const handlePrint = () => window.print();

  async function handleMarkRefunded() {
    setRefundError("");
    const belop = Number(refundAmount);
    if (!(belop > 0) || belop > row.belop) {
      setRefundError(`Beløp må være mellom 0 og ${fmtKr(row.belop)}`);
      return;
    }
    setRefunding(true);
    try {
      const r = await fetch(`${API_BASE_URL}/workshop-services/${row.id}/vipps/mark-refunded`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ belop }),
      });
      const json = await r.json().catch(() => ({}));
      if (!r.ok) {
        setRefundError(json?.message || "Kunne ikke registrere refusjon");
        return;
      }
      setShowRefundForm(false);
      onRefunded?.(row.id, json.payment);
    } catch (e) {
      setRefundError("Kunne ikke registrere refusjon");
    } finally {
      setRefunding(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 print:bg-white print:static print:p-0"
      onClick={onClose}
    >
      <style media="print">{`
        body * { visibility: hidden; }
        #kvittering-print, #kvittering-print * { visibility: visible; }
        #kvittering-print { position: absolute; top: 0; left: 0; width: 100%; }
        #kvittering-print .no-print { display: none !important; }
      `}</style>
      <div
        id="kvittering-print"
        className="w-full max-w-lg rounded-lg bg-white shadow-xl max-h-[90vh] overflow-hidden print:max-h-none print:shadow-none print:rounded-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="no-print flex items-start justify-between border-b px-5 py-4 bg-gray-50">
          <div>
            <div className="text-lg font-semibold">
              Kvittering {row.kvitteringsnummer || <span className="text-gray-400 font-normal">(uten bilagsnummer)</span>}
            </div>
            <div className="text-sm text-gray-500">Serviceordre #{row.serviceRef}</div>
          </div>
          <button onClick={onClose} className="rounded border px-3 py-1.5 text-sm hover:bg-gray-100 shrink-0">
            Lukk
          </button>
        </div>

        <div className="overflow-auto max-h-[70vh] p-5 space-y-4 print:overflow-visible print:max-h-none">
          {/* Print-only header (skjult på skjerm siden det gjentar toppfeltet over) */}
          <div className="hidden print:block">
            <div className="text-lg font-semibold">
              Kvittering {row.kvitteringsnummer || "(uten bilagsnummer)"}
            </div>
            <div className="text-sm text-gray-500">Serviceordre #{row.serviceRef}</div>
          </div>

          <div>
            <div className="font-medium">{row.customerName}</div>
            {row.produktnavn && <div className="text-sm text-gray-600">{row.produktnavn}</div>}
          </div>

          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${RECEIPT_STATUS_STYLE[status]}`}>
              {RECEIPT_STATUS_LABEL[status]}
            </span>
            <span className="text-gray-500">via {METODE_LABEL[row.metode] || row.metode}</span>
            <span className="text-gray-400">·</span>
            <span className="text-gray-500">{fmtDateTime(row.betaltAt)}</span>
            <span className="text-gray-400">·</span>
            <span className="text-gray-500">{prettifyStoreName(row.butikk) || "—"}</span>
          </div>

          {row.serviceLines?.length > 0 && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Arbeid</div>
              <ul className="divide-y">
                {row.serviceLines.map((l, i) => (
                  <li key={i} className="py-2 flex items-start justify-between gap-3 text-sm">
                    <div>
                      <div className="font-medium">{l.navn}</div>
                      {l.kommentar && <div className="text-xs text-gray-500">{l.kommentar}</div>}
                    </div>
                    <div
                      className={`shrink-0 font-medium ${l.reklamasjon || l.gratisarbeid ? "text-gray-400 line-through" : ""
                        }`}
                    >
                      {fmtKr(l.pris)}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {row.partsLines?.length > 0 && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Deler</div>
              <ul className="divide-y">
                {row.partsLines.map((p, i) => (
                  <li key={i} className="py-2 flex items-start justify-between gap-3 text-sm">
                    <div>
                      <div className="font-medium">{p.product}{p.brand ? ` (${p.brand})` : ""}</div>
                      <div className="text-xs text-gray-500">
                        {p.ean ? `Varenr. ${p.ean} · ` : ""}{p.qty} stk · {fmtKr(p.price)}
                      </div>
                    </div>
                    <div className="shrink-0 font-medium">{fmtKr(p.lineTotal)}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {row.fraktLines?.length > 0 && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Frakt</div>
              <ul className="divide-y">
                {row.fraktLines.map((f, i) => (
                  <li key={i} className="py-2 flex items-start justify-between gap-3 text-sm">
                    <div>
                      <div className="font-medium">{f.beskrivelse || "Frakt"}</div>
                      {f.kommentar && <div className="text-xs text-gray-500">{f.kommentar}</div>}
                    </div>
                    <div className="shrink-0 font-medium">{fmtKr(f.pris)}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="text-sm text-gray-600 space-y-1 pt-1">
            <div className="flex justify-between">
              <span>Sum arbeid</span>
              <span>{fmtKr(row.sumArbeid)}</span>
            </div>
            <div className="flex justify-between">
              <span>Sum deler</span>
              <span>{fmtKr(row.sumDeler)}</span>
            </div>
            {row.sumFrakt > 0 && (
              <div className="flex justify-between">
                <span>Sum frakt</span>
                <span>{fmtKr(row.sumFrakt)}</span>
              </div>
            )}
          </div>

          <div className="border-t pt-3 text-sm space-y-1">
            <div className="flex justify-between">
              <span>Sum eks. mva</span>
              <span>{fmtKr2(row.sumEksMva)}</span>
            </div>
            <div className="flex justify-between">
              <span>Mva {row.mvaSats ?? 25} %</span>
              <span>{fmtKr2(row.mvaBelop)}</span>
            </div>
            <div className="flex justify-between font-bold text-base pt-1">
              <span>TOTALT</span>
              <span>{fmtKr2(row.totalInkMva)}</span>
            </div>
          </div>

          <div className="border-t-2 pt-3 flex justify-between items-baseline">
            <span className="font-semibold text-base">Betalt</span>
            <span className="font-bold text-xl">{fmtKr2(row.belop)}</span>
          </div>

          {row.refundertBelop > 0 && (
            <div className="flex justify-between items-baseline text-orange-700">
              <span className="font-semibold text-sm">Refundert</span>
              <span className="font-bold">{fmtKr2(row.refundertBelop)}</span>
            </div>
          )}

          {showDetails && (
            <div className="p-3 bg-gray-50 rounded border text-sm space-y-1">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Betalingsdetaljer</div>
              <div className="flex justify-between"><span className="text-gray-500">Betalingsleverandør</span><span>{METODE_LABEL[row.metode] || row.metode}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Transaksjons-ID</span><span className="font-mono text-xs break-all text-right ml-2">{row.betalingsreferanse || "—"}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Tidspunkt</span><span>{fmtDateTime(row.betaltAt)}</span></div>
              {row.customerPhone && (
                <div className="flex justify-between"><span className="text-gray-500">Telefon</span><span>{row.customerPhone}</span></div>
              )}
              {row.refundertBelop > 0 && (
                <div className="flex justify-between text-orange-700"><span>Refundert beløp</span><span>{fmtKr2(row.refundertBelop)}</span></div>
              )}
            </div>
          )}

          <div className="border-t pt-3 text-xs text-gray-500">
            <div>{selger?.navn || "Røyken Sykkelservice"}</div>
            {selger?.orgnr && (
              <div>Org.nr. {selger.orgnr}{selger.mvaRegistrert ? " MVA" : ""}</div>
            )}
          </div>

          <div className="no-print flex flex-wrap gap-2 pt-2">
            <button
              onClick={() => setShowDetails((v) => !v)}
              className="px-3 py-1.5 rounded border text-sm hover:bg-gray-50"
            >
              {showDetails ? "Skjul betalingsdetaljer" : "Vis betalingsdetaljer"}
            </button>
            <button
              onClick={handlePrint}
              className="px-3 py-1.5 rounded border text-sm hover:bg-gray-50"
            >
              Last ned / skriv ut kvittering
            </button>
            {status === "betalt" && (
              <button
                onClick={() => setShowRefundForm((v) => !v)}
                className="px-3 py-1.5 rounded border border-orange-300 text-orange-700 text-sm hover:bg-orange-50"
              >
                Marker som refundert
              </button>
            )}
          </div>

          {status === "betalt" && showRefundForm && (
            <div className="no-print p-3 bg-orange-50 rounded border border-orange-200 text-sm space-y-2">
              <p className="text-orange-800">
                Registrer at denne betalingen er refundert (f.eks. via Vipps-porteføljen direkte).
                Dette oppdaterer ikke Vipps selv — kun statistikken her.
              </p>
              <div className="flex items-center gap-2">
                <label className="text-orange-800">Beløp:</label>
                <input
                  type="number"
                  min={0}
                  max={row.belop}
                  step="0.01"
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  className="border rounded px-2 py-1 w-28 text-right"
                />
                <span className="text-orange-800">kr</span>
              </div>
              {refundError && <p className="text-red-600">{refundError}</p>}
              <div className="flex gap-2">
                <button
                  onClick={handleMarkRefunded}
                  disabled={refunding}
                  className="px-3 py-1.5 rounded bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-60"
                >
                  {refunding ? "Registrerer…" : "Bekreft refusjon"}
                </button>
                <button
                  onClick={() => setShowRefundForm(false)}
                  className="px-3 py-1.5 rounded border hover:bg-gray-50"
                >
                  Avbryt
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ComingSoonCard({ title, description }) {
  return (
    <div className="rounded border-2 border-dashed p-6 text-center text-gray-500 bg-gray-50">
      <div className="font-semibold text-gray-600">{title} — kommer</div>
      <p className="text-sm mt-1 max-w-lg mx-auto">{description}</p>
    </div>
  );
}

export default function WorkshopSalgsrapportTab() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [monthOffset, setMonthOffset] = useState(0);
  const [storeSlug, setStoreSlug] = useState("alle");
  const [selectedRow, setSelectedRow] = useState(null);
  const [view, setView] = useState("oversikt"); // oversikt | journal | betalinger | oppgjor | varekjop

  const today = useMemo(() => new Date(), []);
  const monthDate = useMemo(
    () => new Date(today.getFullYear(), today.getMonth() + monthOffset, 1),
    [today, monthOffset]
  );
  const year = monthDate.getFullYear();
  const monthIndex0 = monthDate.getMonth();
  const isCurrentMonth = monthOffset === 0;

  const firstDayISO = useMemo(() => isoDate(new Date(year, monthIndex0, 1)), [year, monthIndex0]);
  const lastDayISO = useMemo(
    () => isoDate(new Date(year, monthIndex0, daysInMonth(year, monthIndex0))),
    [year, monthIndex0]
  );

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        const r = await fetch(
          `${API_BASE_URL}/workshop/payments?verkstedSlug=${encodeURIComponent(storeSlug)}&from=${firstDayISO}&to=${lastDayISO}`
        );
        const data = r.ok ? await r.json() : { rows: [] };
        setRows(data.rows || []);
      } catch (err) {
        console.error("Feil ved henting av salgsrapport:", err);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [firstDayISO, lastDayISO, storeSlug]);

  const summary = useMemo(() => {
    let sumArbeid = 0, sumDeler = 0, sumFrakt = 0, sumEksMva = 0, mvaBelop = 0, totaltSalg = 0, refundert = 0;
    const perButikk = {};
    const perMetode = {};

    for (const r of rows) {
      sumArbeid += Number(r.sumArbeid) || 0;
      sumDeler += Number(r.sumDeler) || 0;
      sumFrakt += Number(r.sumFrakt) || 0;
      sumEksMva += Number(r.sumEksMva) || 0;
      mvaBelop += Number(r.mvaBelop) || 0;
      totaltSalg += Number(r.totalInkMva) || 0;
      refundert += Number(r.refundertBelop) || 0;

      const bKey = r.butikk || "ukjent";
      if (!perButikk[bKey]) perButikk[bKey] = { antall: 0, arbeid: 0, deler: 0, frakt: 0, totalt: 0 };
      perButikk[bKey].antall += 1;
      perButikk[bKey].arbeid += Number(r.sumArbeid) || 0;
      perButikk[bKey].deler += Number(r.sumDeler) || 0;
      perButikk[bKey].frakt += Number(r.sumFrakt) || 0;
      perButikk[bKey].totalt += Number(r.totalInkMva) || 0;

      const mKey = r.metode || "ukjent";
      if (!perMetode[mKey]) perMetode[mKey] = { antall: 0, brutto: 0 };
      perMetode[mKey].antall += 1;
      perMetode[mKey].brutto += Number(r.belop) || 0;
    }

    return {
      antall: rows.length,
      sumArbeid: round2(sumArbeid),
      sumDeler: round2(sumDeler),
      sumFrakt: round2(sumFrakt),
      totaltSalg: round2(totaltSalg),
      nettoSalg: round2(sumEksMva),
      mvaBelop: round2(mvaBelop),
      refundert: round2(refundert),
      perButikk,
      perMetode,
    };
  }, [rows]);

  function exportSalgsjournal() {
    const headers = ["Bilag", "Dato", "Ordre", "Kunde", "Butikk", "Arbeid", "Deler", "Frakt", "Eks. mva", "Mva", "Totalt", "Betaling", "Status"];
    const data = rows.map((r) => [
      r.kvitteringsnummer || "",
      fmtDate(r.betaltAt),
      r.serviceRef,
      r.customerName,
      prettifyStoreName(r.butikk) || r.butikk,
      csvNum(r.sumArbeid),
      csvNum(r.sumDeler),
      csvNum(r.sumFrakt),
      csvNum(r.sumEksMva),
      csvNum(r.mvaBelop),
      csvNum(r.totalInkMva),
      METODE_LABEL[r.metode] || r.metode,
      STATUS_LABEL[r.status] || r.status,
    ]);
    downloadCsv(`salgsjournal-${year}-${String(monthIndex0 + 1).padStart(2, "0")}.csv`, headers, data);
  }

  function exportBetalingsjournal() {
    const headers = ["Dato", "Kvittering", "Ordre", "Metode", "Transaksjons-ID", "Beløp", "Status"];
    const data = rows.map((r) => [
      fmtDate(r.betaltAt),
      r.kvitteringsnummer || "",
      r.serviceRef,
      METODE_LABEL[r.metode] || r.metode,
      r.betalingsreferanse || "",
      csvNum(r.belop),
      STATUS_LABEL[r.status] || r.status,
    ]);
    downloadCsv(`betalingsjournal-${year}-${String(monthIndex0 + 1).padStart(2, "0")}.csv`, headers, data);
  }

  function handleRefunded(rowId, payment) {
    setRows((prev) =>
      prev.map((r) =>
        r.id === rowId
          ? { ...r, status: payment.status, refundertBelop: payment.refundertBelop }
          : r
      )
    );
    setSelectedRow((prev) =>
      prev && prev.id === rowId
        ? { ...prev, status: payment.status, refundertBelop: payment.refundertBelop }
        : prev
    );
  }

  if (loading) return <div className="py-10 text-center">Laster salgsrapport…</div>;

  return (
    <div className="space-y-6">
      <PaymentDetailModal row={selectedRow} onClose={() => setSelectedRow(null)} onRefunded={handleRefunded} />

      {/* Header med måneds- og butikkvalg */}
      <div className="rounded border bg-green-50 p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-sm text-gray-600">Salgsrapport</div>
            <div className="text-xl font-bold">{MONTH_NAMES[monthIndex0]} {year}</div>
            <div className="text-sm text-gray-700 mt-1">{firstDayISO} – {lastDayISO}</div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <select
              value={storeSlug}
              onChange={(e) => setStoreSlug(e.target.value)}
              className="border rounded px-2 py-1.5 text-sm bg-white"
            >
              <option value="alle">Alle verksteder</option>
              {ALL_STORES.map((slug) => (
                <option key={slug} value={slug}>{prettifyStoreName(slug)}</option>
              ))}
            </select>
            <button
              onClick={() => setMonthOffset((o) => o - 1)}
              className="px-3 py-1.5 rounded border bg-white hover:bg-gray-50 text-sm"
            >
              ← Forrige
            </button>
            {!isCurrentMonth && (
              <button
                onClick={() => setMonthOffset(0)}
                className="px-3 py-1.5 rounded border bg-white hover:bg-gray-50 text-sm text-green-700"
              >
                Denne måneden
              </button>
            )}
            <button
              onClick={() => setMonthOffset((o) => Math.min(0, o + 1))}
              disabled={isCurrentMonth}
              className="px-3 py-1.5 rounded border bg-white hover:bg-gray-50 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Neste →
            </button>
          </div>
        </div>
      </div>

      <div className="text-xs text-gray-400">
        Viser foreløpig kun Vipps-betalinger. Kortterminal kommer som egen kilde inn i samme rapport senere.
      </div>

      {/* Undernavigasjon */}
      <div className="flex flex-wrap gap-2 border-b pb-3">
        {[
          ["oversikt", "Oversikt"],
          ["journal", "Salgsjournal"],
          ["betalinger", "Betalinger"],
          ["oppgjor", "Oppgjør"],
          ["varekjop", "Varekjøp"],
        ].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setView(key)}
            className={`px-4 py-2 rounded text-sm ${
              view === key ? "bg-blue-600 text-white" : "bg-gray-100 hover:bg-gray-200"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* OVERSIKT */}
      {view === "oversikt" && (
        <div className="space-y-6">
          <div className="overflow-x-auto">
            <table className="min-w-full max-w-md border rounded bg-white text-sm">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="px-3 py-2 text-left"></th>
                  <th className="px-3 py-2 text-right">Beløp inkl. mva</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t"><td className="px-3 py-2">Arbeid</td><td className="px-3 py-2 text-right">{fmtKr(summary.sumArbeid)}</td></tr>
                <tr className="border-t"><td className="px-3 py-2">Deler</td><td className="px-3 py-2 text-right">{fmtKr(summary.sumDeler)}</td></tr>
                {summary.sumFrakt > 0 && (
                  <tr className="border-t"><td className="px-3 py-2">Frakt</td><td className="px-3 py-2 text-right">{fmtKr(summary.sumFrakt)}</td></tr>
                )}
                <tr className="border-t font-semibold"><td className="px-3 py-2">Totalt salg</td><td className="px-3 py-2 text-right">{fmtKr(summary.totaltSalg)}</td></tr>
                <tr className="border-t"><td className="px-3 py-2">Netto salg (eks. mva)</td><td className="px-3 py-2 text-right">{fmtKr(summary.nettoSalg)}</td></tr>
                <tr className="border-t"><td className="px-3 py-2">Utgående mva 25 %</td><td className="px-3 py-2 text-right">{fmtKr(summary.mvaBelop)}</td></tr>
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="rounded border p-4 bg-white">
              <div className="text-xs text-gray-500 uppercase tracking-wide">Antall salg</div>
              <div className="text-2xl font-bold mt-1">{summary.antall}</div>
            </div>
            {summary.refundert > 0 && (
              <div className="rounded border p-4 bg-orange-50">
                <div className="text-xs text-gray-500 uppercase tracking-wide">Krediteringer/refusjoner</div>
                <div className="text-2xl font-bold mt-1 text-orange-700">− {fmtKr(summary.refundert)}</div>
              </div>
            )}
            <div className="rounded border p-4 bg-blue-50 col-span-2 md:col-span-1">
              <div className="text-xs text-gray-500 uppercase tracking-wide">Totalt salg etter krediteringer</div>
              <div className="text-2xl font-bold mt-1">{fmtKr(summary.totaltSalg - summary.refundert)}</div>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Salg per avdeling</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full border rounded bg-white text-sm">
                <thead className="bg-gray-100 text-gray-700">
                  <tr>
                    <th className="px-3 py-2 text-left">Avdeling</th>
                    <th className="px-3 py-2 text-right">Antall</th>
                    <th className="px-3 py-2 text-right">Arbeid</th>
                    <th className="px-3 py-2 text-right">Deler</th>
                    <th className="px-3 py-2 text-right">Frakt</th>
                    <th className="px-3 py-2 text-right">Totalt</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(summary.perButikk).map(([slug, v]) => (
                    <tr key={slug} className="border-t">
                      <td className="px-3 py-2">{prettifyStoreName(slug) || slug}</td>
                      <td className="px-3 py-2 text-right">{v.antall}</td>
                      <td className="px-3 py-2 text-right">{fmtKr(v.arbeid)}</td>
                      <td className="px-3 py-2 text-right">{fmtKr(v.deler)}</td>
                      <td className="px-3 py-2 text-right">{fmtKr(v.frakt)}</td>
                      <td className="px-3 py-2 text-right font-medium">{fmtKr(v.totalt)}</td>
                    </tr>
                  ))}
                  {Object.keys(summary.perButikk).length === 0 && (
                    <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-500">Ingen salg denne måneden</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* SALGSJOURNAL */}
      {view === "journal" && (
        <div className="space-y-3">
          <div className="flex justify-end">
            <button onClick={exportSalgsjournal} className="px-3 py-1.5 rounded border text-sm bg-white hover:bg-gray-50">
              Last ned salgsjournal (CSV)
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full border rounded bg-white text-sm">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="px-3 py-2 text-left">Bilag</th>
                  <th className="px-3 py-2 text-left">Dato</th>
                  <th className="px-3 py-2 text-left">Ordre</th>
                  <th className="px-3 py-2 text-right">Arbeid</th>
                  <th className="px-3 py-2 text-right">Deler</th>
                  <th className="px-3 py-2 text-right">Frakt</th>
                  <th className="px-3 py-2 text-right">Eks. mva</th>
                  <th className="px-3 py-2 text-right">Mva</th>
                  <th className="px-3 py-2 text-right">Totalt</th>
                  <th className="px-3 py-2 text-left">Betaling</th>
                </tr>
              </thead>
              <tbody>
                {rows.length ? rows.map((r) => (
                  <tr key={r.id} className="border-t hover:bg-blue-50 cursor-pointer" onClick={() => setSelectedRow(r)} title="Klikk for kvitteringsutdrag">
                    <td className="px-3 py-2 font-mono text-xs">{r.kvitteringsnummer || "—"}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{fmtDate(r.betaltAt)}</td>
                    <td className="px-3 py-2 font-mono">{r.serviceRef}</td>
                    <td className="px-3 py-2 text-right">{fmtKr(r.sumArbeid)}</td>
                    <td className="px-3 py-2 text-right">{fmtKr(r.sumDeler)}</td>
                    <td className="px-3 py-2 text-right">{fmtKr(r.sumFrakt)}</td>
                    <td className="px-3 py-2 text-right">{fmtKr2(r.sumEksMva)}</td>
                    <td className="px-3 py-2 text-right">{fmtKr2(r.mvaBelop)}</td>
                    <td className="px-3 py-2 text-right font-medium">{fmtKr2(r.totalInkMva)}</td>
                    <td className="px-3 py-2">{METODE_LABEL[r.metode] || r.metode}</td>
                  </tr>
                )) : (
                  <tr><td colSpan={10} className="px-4 py-6 text-center text-gray-500">Ingen salg registrert denne måneden</td></tr>
                )}
              </tbody>
              {rows.length > 0 && (
                <tfoot className="border-t bg-gray-50 font-semibold">
                  <tr>
                    <td colSpan={3} className="px-3 py-2">Sum</td>
                    <td className="px-3 py-2 text-right">{fmtKr(summary.sumArbeid)}</td>
                    <td className="px-3 py-2 text-right">{fmtKr(summary.sumDeler)}</td>
                    <td className="px-3 py-2 text-right">{fmtKr(summary.sumFrakt)}</td>
                    <td className="px-3 py-2 text-right">{fmtKr2(summary.nettoSalg)}</td>
                    <td className="px-3 py-2 text-right">{fmtKr2(summary.mvaBelop)}</td>
                    <td className="px-3 py-2 text-right">{fmtKr2(summary.totaltSalg)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {/* BETALINGER */}
      {view === "betalinger" && (
        <div className="space-y-6">
          <div className="overflow-x-auto">
            <table className="min-w-full max-w-md border rounded bg-white text-sm">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="px-3 py-2 text-left">Metode</th>
                  <th className="px-3 py-2 text-right">Antall</th>
                  <th className="px-3 py-2 text-right">Brutto</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(summary.perMetode).map(([m, v]) => (
                  <tr key={m} className="border-t">
                    <td className="px-3 py-2">{METODE_LABEL[m] || m}</td>
                    <td className="px-3 py-2 text-right">{v.antall}</td>
                    <td className="px-3 py-2 text-right">{fmtKr(v.brutto)}</td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={3} className="px-3 py-6 text-center text-gray-500">Ingen betalinger denne måneden</td></tr>
                )}
              </tbody>
              {rows.length > 0 && (
                <tfoot className="border-t bg-gray-50 font-semibold">
                  <tr>
                    <td className="px-3 py-2">Totalt</td>
                    <td className="px-3 py-2 text-right">{summary.antall}</td>
                    <td className="px-3 py-2 text-right">{fmtKr(summary.totaltSalg)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          <div className="flex justify-end">
            <button onClick={exportBetalingsjournal} className="px-3 py-1.5 rounded border text-sm bg-white hover:bg-gray-50">
              Last ned betalingsjournal (CSV)
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full border rounded bg-white text-sm">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="px-3 py-2 text-left">Dato</th>
                  <th className="px-3 py-2 text-left">Kvittering</th>
                  <th className="px-3 py-2 text-left">Ordre</th>
                  <th className="px-3 py-2 text-left">Metode</th>
                  <th className="px-3 py-2 text-left">Transaksjons-ID</th>
                  <th className="px-3 py-2 text-right">Beløp</th>
                </tr>
              </thead>
              <tbody>
                {rows.length ? rows.map((r) => (
                  <tr key={r.id} className="border-t hover:bg-blue-50 cursor-pointer" onClick={() => setSelectedRow(r)} title="Klikk for kvitteringsutdrag">
                    <td className="px-3 py-2 whitespace-nowrap">{fmtDate(r.betaltAt)}</td>
                    <td className="px-3 py-2 font-mono text-xs">{r.kvitteringsnummer || "—"}</td>
                    <td className="px-3 py-2 font-mono">{r.serviceRef}</td>
                    <td className="px-3 py-2">{METODE_LABEL[r.metode] || r.metode}</td>
                    <td className="px-3 py-2 font-mono text-xs break-all">{r.betalingsreferanse || "—"}</td>
                    <td className="px-3 py-2 text-right font-medium">
                      {fmtKr(r.belop)}
                      {r.refundertBelop > 0 && (
                        <span className="block text-xs text-orange-600">− {fmtKr(r.refundertBelop)} refundert</span>
                      )}
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-500">Ingen betalinger registrert denne måneden</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* OPPGJØR (kommer) */}
      {view === "oppgjor" && (
        <ComingSoonCard
          title="Oppgjørsrapport"
          description="Avstemming mot Vipps' faktiske utbetaling (brutto, gebyrer, netto til bank). Krever tilgang til Vipps' Settlement Report API — et eget API-scope vi ikke har testnøkler til ennå."
        />
      )}

      {/* VAREKJØP (kommer) */}
      {view === "varekjop" && (
        <ComingSoonCard
          title="Varekjøp / vareoverføringer"
          description="Oversikt over deler levert fra Sport 1 Røyken/Slemmestad til Røyken Sykkelservice, som grunnlag for intern fakturering. Krever at systemet begynner å spore kostpris per del, i tillegg til dagens utsalgspris."
        />
      )}
    </div>
  );
}
